import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    hasModuleAccess,
    hasAnyRole,
    isAuthenticated
} from "../utils/authUtils";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function Sidebar({ isOpen, setIsOpen }) {
    const location = useLocation();

    /* ===== Setup menu expanded by default ===== */
    const [isSetupOpen, setIsSetupOpen] = useState(true);
    const [isEntitiesOpen, setIsEntitiesOpen] = useState(true); // Open by default
    const [entities, setEntities] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(true);

    // Function to refresh entities that can be called from outside
    const refreshEntities = async () => {
        if (!hasModuleAccess("entities")) return;
        
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            
            console.log("Refreshing entities...");
            
            // Get current user and check role
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const userRoles = currentUser?.roles || [];
            const isSuperAdmin = userRoles.includes('superadmin');
            
            // Superadmin sees all entities; all other roles see only their assigned entities
            const endpoint = isSuperAdmin ? '/entities' : '/entities/my';
            const res = await axios.get(`${API_BASE}${endpoint}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log("Entities refreshed:", res.data);
            setEntities(res.data || []);
        } catch (error) {
            console.error("Failed to refresh entities:", error);
        }
    };

    // Make refresh function available globally
    if (typeof window !== 'undefined') {
        window.refreshSidebarEntities = refreshEntities;
    }

    const setupItems = [
        {
            key: "users",
            label: "Users",
            path: "/users/list",
            icon: "icon-users"
        },
        {
            key: "roles",
            label: "Roles & Permissions",
            path: "/roles/list",
            icon: "icon-lock"
        },
        {
            key: "entities",
            label: "Entities",
            path: "/entities/list",
            icon: "icon-briefcase"
        }
    ];

    // Filter setup items based on both module access and user role
    const allowedSetupItems = setupItems.filter(item => {
        if (!hasModuleAccess(item.key)) return false;
        
        // Hide Users menu for employees
        if (item.key === 'users' && hasAnyRole(['employee'])) {
            return false;
        }
        
        return true;
    });

    const setupRoutes = allowedSetupItems.map(item =>
        item.path.split("/")[1]
    );

    const isSetupRouteActive = setupRoutes.some(route =>
        location.pathname.startsWith(`/${route}`)
    );

    /* ===== Fetch entities for menu ===== */
    useEffect(() => {
        const fetchEntities = async () => {
            console.log("Checking entities access:", hasModuleAccess("entities"));
            if (!hasModuleAccess("entities")) {
                console.log("No access to entities module");
                setEntitiesLoading(false);
                return;
            }
            
            try {
                setEntitiesLoading(true);
                console.log("Fetching entities for sidebar...");
                const token = localStorage.getItem("token");
                if (!token) {
                    console.log("No token found");
                    setEntitiesLoading(false);
                    return;
                }
                console.log("Using token:", token.substring(0, 50) + "...");
                
                // Get current user and check role
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const userRoles = currentUser?.roles || [];
                const isSuperAdmin = userRoles.includes('superadmin');
                
                // Superadmin sees all entities; all other roles see only their assigned entities
                const endpoint = isSuperAdmin ? '/entities' : '/entities/my';
                const res = await axios.get(`${API_BASE}${endpoint}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                console.log("Entities API response:", res.status, res.data);
                console.log("Setting entities to:", res.data || []);
                setEntities(res.data || []);
            } catch (error) {
                console.error("Failed to fetch entities for sidebar:", error);
                if (error.response?.status === 401) {
                    console.log("Authentication failed - clearing entities");
                    setEntities([]);
                }
            } finally {
                setEntitiesLoading(false);
            }
        };

        fetchEntities();
    }, []); // Remove hasModuleAccess dependency to avoid infinite loops

    /* ===== Auto-close setup on navigation ===== */
    useEffect(() => {
        // setIsSetupOpen(false); // Keep setup open by default
        // Keep entities open by default, don't auto-close
        // setIsEntitiesOpen(false);
    }, [location.pathname]);

    /* ===== Handle window resize ===== */
    useEffect(() => {
        const handleResize = () => {
            // Auto-close sidebar when transitioning to desktop view
            if (window.innerWidth >= 992 && isOpen && setIsOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, setIsOpen]);

    if (!isAuthenticated() || location.pathname === "/unauthorized") {
        return null;
    }

    const closeOnMobile = () => {
        if (window.innerWidth < 992 && setIsOpen) {
            setIsOpen(false);
        }
    };

    /* ===== DASHBOARD ACTIVE LOGIC ===== */
    const isEntityDetails = location.pathname.startsWith("/entities/");

    return (
        <>
            <style>{`
                .main-menu {
                    width: 260px;
                    height: 100vh;
                    background: #fff;
                    position: fixed;
                    top: 0;
                    left: 0;
                    overflow-y: auto;
                    transition: transform 0.3s ease;
                    z-index: 999;
                    box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                }

                /* Desktop view - sidebar always visible */
                @media (min-width: 992px) {
                    .main-menu {
                        transform: translateX(0);
                        position: fixed;
                        height: calc(100vh - 4rem);
                        top: 4rem;
                        left: 0;
                        z-index: 999;
                    }
                }

                /* Tablet and mobile - sidebar hidden by default */
                @media (max-width: 991px) {
                    .main-menu {
                        transform: translateX(-100%);
                        width: 280px;
                        height: 100vh;
                        top: 0;
                        z-index: 1050;
                    }
                    
                    .main-menu.open {
                        transform: translateX(0);
                    }
                }

                /* Mobile specific adjustments */
                @media (max-width: 575px) {
                    .main-menu {
                        width: 100%;
                        max-width: 280px;
                    }
                    
                    .nav-link {
                        padding: 14px 16px !important;
                        font-size: 14px;
                    }
                    
                    .nav-link i {
                        font-size: 16px !important;
                        margin-right: 12px;
                    }
                    
                    .navigation-header {
                        padding: 10px 16px !important;
                        font-size: 11px;
                    }
                    
                    .menu-content .nav-link {
                        padding: 12px 20px 12px 36px !important;
                        font-size: 13px;
                    }
                    
                    .badge {
                        font-size: 10px;
                        padding: 2px 6px;
                    }
                }

                /* Overlay for mobile and tablet */
                .sidebar-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 1040;
                    display: none;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                @media (max-width: 991px) {
                    .sidebar-overlay.show {
                        display: block;
                        opacity: 1;
                    }
                }

                .main-menu .nav-link {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 20px;
                    color: #6e6b7b;
                    border-radius: 4px;
                    transition: all 0.25s ease;
                    text-decoration: none;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }

                .main-menu .nav-link i {
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .main-menu .nav-link:hover {
                    background-color: #f4f5fa;
                    color: #7367f0;
                }

                .main-menu .nav-link.active {
                    background-color: #7367f0;
                    color: #fff;
                    font-weight: 600;
                }

                .navigation-header {
                    padding: 12px 20px;
                    font-size: 12px;
                    text-transform: uppercase;
                    color: #a5a3ae;
                    font-weight: 600;
                }

                .nav-item {
                    margin: 4px 0;
                }

                .has-sub > .nav-link {
                    justify-content: space-between;
                    cursor: pointer;
                }

                .menu-content {
                    max-height: 0;
                    overflow: hidden;
                    padding-left: 20px;
                    transition: max-height 0.3s ease;
                }

                .has-sub.open .menu-content {
                    max-height: 500px;
                }

                .has-sub .icon-chevron-down {
                    transition: transform 0.3s ease;
                    flex-shrink: 0;
                }

                .has-sub.open .icon-chevron-down {
                    transform: rotate(180deg);
                }

                .menu-content .nav-link {
                    padding: 10px 20px;
                    font-size: 14px;
                }

                .menu-title {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex-grow: 1;
                }

                /* Touch-friendly improvements */
                @media (max-width: 767px) {
                    .nav-link {
                        min-height: 44px;
                        touch-action: manipulation;
                    }
                    
                    .has-sub > .nav-link {
                        min-height: 48px;
                    }
                }

                /* Ensure proper scrolling */
                .main-menu-content {
                    height: 100%;
                    overflow-y: auto;
                    overflow-x: hidden;
                }

                .navigation {
                    list-style: none;
                    padding: 20px 0;
                    margin: 0;
                }

                .has-sub .icon-chevron-down {
                    transition: transform 0.3s ease;
                    flex-shrink: 0;
                }

                .has-sub.open .icon-chevron-down {
                    transform: rotate(180deg);
                }

                .menu-content .nav-link {
                    padding: 10px 20px;
                    font-size: 14px;
                }

                .menu-title {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex-grow: 1;
                }

                /* Touch-friendly improvements */
                @media (max-width: 767px) {
                    .nav-link {
                        min-height: 44px;
                        touch-action: manipulation;
                    }
                    
                    .has-sub > .nav-link {
                        min-height: 48px;
                    }
                }
            `}</style>

            {/* Mobile overlay */}
            <div 
                className={`sidebar-overlay ${isOpen ? 'show' : ''}`}
                onClick={() => setIsOpen && setIsOpen(false)}
            />

            <div
                className={`main-menu menu-fixed menu-light menu-accordion menu-shadow ${isOpen ? "open" : ""
                    }`}
            >
                <div className="main-menu-content">
                    <ul className="navigation navigation-main">
                        <li className="navigation-header">
                            <span>General</span>
                        </li>

                        {/* ================= ADMIN DASHBOARD ================= */}
                        {hasAnyRole(["superadmin", "admin", "manager"]) && (
                            <li className="nav-item">
                                <NavLink
                                    to="/admins/dashboard"
                                    className={() =>
                                        `nav-link ${location.pathname.startsWith("/admins/dashboard") ||
                                            isEntityDetails
                                            ? "active"
                                            : ""
                                        }`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-home" />
                                    <span className="menu-title">Dashboard</span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= TEAM LOCATIONS (superadmin only) ================= */}
                        {hasAnyRole(["superadmin"]) && (
                            <li className="nav-item">
                                <NavLink
                                    to="/admins/attendance-map"
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? "active" : ""}`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-map-pin" />
                                    <span className="menu-title">Team Locations</span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= CUSTOMER DASHBOARD ================= */}
                        {hasAnyRole(["customer"]) && (
                            <li className="nav-item">
                                <NavLink
                                    to="/customers/dashboard"
                                    className={() =>
                                        `nav-link ${location.pathname.startsWith("/customers/dashboard") ||
                                            isEntityDetails
                                            ? "active"
                                            : ""
                                        }`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-home" />
                                    <span className="menu-title">
                                        Customer Dashboard
                                    </span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= EMPLOYEE DASHBOARD ================= */}
                        {hasAnyRole(["employee"]) && !hasAnyRole(["superadmin", "admin", "manager"]) && (
                            <li className="nav-item">
                                <NavLink
                                    to="/employees/dashboard"
                                    className={() =>
                                        `nav-link ${location.pathname.startsWith("/employees/dashboard") ||
                                            isEntityDetails
                                            ? "active"
                                            : ""
                                        }`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-home" />
                                    <span className="menu-title">
                                        Employee Dashboard
                                    </span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= ENTITIES ================= */}
                        {hasModuleAccess("entities") && (
                            <li className={`nav-item has-sub ${isEntitiesOpen ? "open" : ""}`}>
                                <a
                                    href="#!"
                                    className={`nav-link ${location.pathname.startsWith("/entities/") ? "active" : ""}`}
                                    onClick={() => setIsEntitiesOpen(prev => !prev)}
                                >
                                    <i className="feather icon-briefcase" />
                                    <span className="menu-title">Entities</span>
                                    <i className="feather icon-chevron-down ms-auto" />
                                </a>

                                <ul className="menu-content">
                                    <li>
                                        <NavLink
                                            to="/entities/list"
                                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                                            onClick={closeOnMobile}
                                        >
                                            <i className="feather icon-list" />
                                            <span className="menu-title">All Entities</span>
                                        </NavLink>
                                    </li>
                                    {entitiesLoading ? (
                                        <li>
                                            <span className="nav-link text-muted">
                                                <i className="feather icon-loader" />
                                                <span className="menu-title">Loading...</span>
                                            </span>
                                        </li>
                                    ) : entities.length === 0 ? (
                                        <li>
                                            <span className="nav-link text-muted" style={{ fontSize: '12px' }}>
                                                <i className="feather icon-info" />
                                                <span className="menu-title">No entities found</span>
                                            </span>
                                        </li>
                                    ) : (
                                        entities.map(entity => (
                                            <li key={entity._id}>
                                                <NavLink
                                                    to={`/entities/${entity._id}`}
                                                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                                                    onClick={closeOnMobile}
                                                    title={entity.entityName}
                                                >
                                                    <i className="feather icon-box" />
                                                    <span className="menu-title">
                                                        {entity.entityName.length > 20 
                                                            ? `${entity.entityName.substring(0, 20)}...` 
                                                            : entity.entityName}
                                                    </span>
                                                </NavLink>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </li>
                        )}

                        {/* ================= REMINDERS (HIDDEN) ================= */}
                        {false && hasModuleAccess("reminders") && (
                            <li className="nav-item">
                                <NavLink
                                    to="/reminders"
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? "active" : ""}`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-bell" />
                                    <span className="menu-title">Reminders</span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= INCIDENTS (HIDDEN) ================= */}
                        {false && hasModuleAccess("incidents") && (
                            <li className="nav-item">
                                <NavLink
                                    to="/incidents"
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? "active" : ""}`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i className="feather icon-alert-triangle" />
                                    <span className="menu-title">Incidents</span>
                                </NavLink>
                            </li>
                        )}

                        {/* ================= SETUP ================= */}
                        {!hasAnyRole(['employee', 'staff']) && allowedSetupItems.length === 1 && (
                            <li className="nav-item">
                                <NavLink
                                    to={allowedSetupItems[0].path}
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? "active" : ""}`
                                    }
                                    onClick={closeOnMobile}
                                >
                                    <i
                                        className={`feather ${allowedSetupItems[0].icon}`}
                                    />
                                    <span className="menu-title">
                                        {allowedSetupItems[0].label}
                                    </span>
                                </NavLink>
                            </li>
                        )}

                        {!hasAnyRole(['employee', 'staff']) && allowedSetupItems.length > 1 && (
                            <li
                                className={`nav-item has-sub ${isSetupOpen ? "open" : ""
                                    }`}
                            >
                                <a
                                    href="#!"
                                    className={`nav-link ${isSetupRouteActive ? "active" : ""
                                        }`}
                                    onClick={() =>
                                        setIsSetupOpen(prev => !prev)
                                    }
                                >
                                    <i className="feather icon-settings" />
                                    <span className="menu-title">Setup</span>
                                    <i className="feather icon-chevron-down ms-auto" />
                                </a>

                                <ul className="menu-content">
                                    {allowedSetupItems.map(item => (
                                        <li key={item.key}>
                                            <NavLink
                                                to={item.path}
                                                className={({ isActive }) =>
                                                    `nav-link ${isActive ? "active" : ""
                                                    }`
                                                }
                                                onClick={closeOnMobile}
                                            >
                                                <i
                                                    className={`feather ${item.icon}`}
                                                />
                                                <span className="menu-title">
                                                    {item.label}
                                                </span>
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </>
    );
}